import type { GuidancePanelProps } from './GuidancePanel';

type GuidanceContent = Omit<GuidancePanelProps, 'screenKey'>;

/**
 * Per-screen guidance copy. Each screen imports the entry matching its
 * route key so the tutorial overlay and the inline GuidancePanel stay in
 * lock-step. Copy deliberately speaks in plain language — no ADR numbers in
 * the user-visible text, but we lean on the concrete mechanics
 * ("3-of-5 signatures", "point-in-time replay", …).
 */
export const GUIDANCE: Record<string, GuidanceContent> = {
  dashboard: {
    eyebrow: '§ GETTING STARTED',
    title: 'Dashboard — validation at a glance',
    intro:
      'See replay parity, drift signals and pending sign-offs for the selected window, straight from the audit pipeline.',
    audience: 'For validation analysts + managers',
    steps: [
      {
        title: 'Pick a time window',
        body: 'Use the time-window selector to scope parity, KPIs and recent runs to 24 h, 7 d, or 30 d.',
      },
      {
        title: 'Scan the KPI strip',
        body: 'Parity, replays, open divergences and sealed packages tell you whether anything needs your attention right now.',
      },
      {
        title: 'Kick off a replay',
        body: 'Hit "New replay" (or any Quick Action) to regenerate alerts against a fresh snapshot — results appear in Recent runs.',
      },
    ],
    glossary: [
      { term: 'Parity', meaning: 'Byte-for-byte match between regenerated and historical alerts for the window.' },
      { term: 'Replay', meaning: 'Deterministic re-run of scenarios against a frozen data snapshot.' },
      { term: 'Sign-off', meaning: '3-of-5 HSM-anchored signatures sealing evidence for a regulator.' },
      { term: 'Drift flag', meaning: 'A rule whose behaviour moved enough to warrant a look before the next run.' },
    ],
  },

  scenarios: {
    eyebrow: '§ GETTING STARTED',
    title: 'Scenario library — versioned typologies',
    intro:
      'Every detection scenario your tenant uses, with versions, ownership and drift status in one sortable catalogue.',
    audience: 'For validation analysts + scenario authors',
    steps: [
      {
        title: 'Filter to what you need',
        body: 'Use the status pills (All / Published / Draft / Retired) and the source-platform select to narrow the list.',
      },
      {
        title: 'Open or edit a scenario',
        body: 'Click a row to inspect details; use per-row Edit to change Draft scenarios — Published ones are immutable until versioned.',
      },
      {
        title: 'Create + retire cleanly',
        body: 'Hit "+ New scenario" to add one; Retire (instead of Delete) preserves audit history while removing from rotation.',
      },
    ],
    glossary: [
      { term: 'Draft', meaning: 'Editable working copy — never feeds production runs.' },
      { term: 'Published', meaning: 'Frozen version — every replay pins against it exactly as stored.' },
      { term: 'Retired', meaning: 'No longer in rotation; history retained for audit.' },
      { term: 'Family', meaning: 'Typology bucket (AML, Trade, Sanctions, …) used for coverage rollups.' },
    ],
  },

  replays: {
    eyebrow: '§ GETTING STARTED',
    title: 'Replays — regenerate alerts, prove parity',
    intro:
      'Every replay run regenerates alerts from a frozen transaction snapshot and compares them to the historical record.',
    audience: 'For validation analysts + MLROs',
    steps: [
      {
        title: 'Start a new run',
        body: 'Click "+ New replay run", pick a scenario + version, select the date window and a reference snapshot.',
      },
      {
        title: 'Choose how to compare',
        body: 'Pick one or more modes: byte-identical (strictest), threshold drift, grouping delta, or timing delta.',
      },
      {
        title: 'Review the receipt',
        body: 'Each sealed run produces a parity receipt. Click a row to inspect; use Re-run to queue an equivalent run.',
      },
    ],
    glossary: [
      { term: 'Receipt', meaning: 'Short id (0x…) that uniquely identifies a run and its manifest.' },
      { term: 'Snapshot', meaning: 'Frozen point-in-time view of the transaction fabric feeding the replay.' },
      { term: 'Byte-identical', meaning: 'The regenerated alert matches the historical alert, bit for bit.' },
      { term: 'Threshold drift', meaning: 'Differences inside the rule\'s allowed numeric drift band.' },
    ],
  },

  alerts: {
    eyebrow: '§ GETTING STARTED',
    title: 'Alerts — triage historical + synthetic alerts',
    intro:
      'Live + historical alerts stream in from your upstream engines; synthetic rows are the sealed outputs of a replay run.',
    audience: 'For validation analysts',
    steps: [
      {
        title: 'Filter by disposition or class',
        body: 'Use the filter row to narrow by disposition (Open, Under review, Escalated…), scenario, or alert class.',
      },
      {
        title: 'Open an alert to see supporting data',
        body: 'Click a row to pop the detail drawer — vendor payload, supporting transactions and current disposition are there.',
      },
      {
        title: 'Set a disposition',
        body: 'Use the per-row Triage button to mark Under review, Escalated, Closed (SAR), False positive etc. Reasons feed the audit trail.',
      },
    ],
    glossary: [
      { term: 'Historical', meaning: 'Alert captured verbatim from the upstream engine (Oracle FCCM, SAS, Actimize…).' },
      { term: 'Synthetic', meaning: 'Alert regenerated by a Sentinel replay (display id SYN-…).' },
      { term: 'Disposition', meaning: 'Analyst verdict: Under review, Escalated, Closed-SAR, Closed-no-action, False positive.' },
      { term: 'Grouping key', meaning: 'Upstream key that bundles related alerts into a single case.' },
    ],
  },

  divergence: {
    eyebrow: '§ GETTING STARTED',
    title: 'Divergence map — where replays differ',
    intro:
      'Every byte-level disagreement between replay output and the historical record, bucketed into four outcomes.',
    audience: 'For validation analysts + scenario authors',
    steps: [
      {
        title: 'Filter by outcome',
        body: 'Use the pills or the Outcome select to show only Matched / Missed / Extra / Threshold-drift rows.',
      },
      {
        title: 'Expand "why" for root cause',
        body: 'Each row has a "why" toggle that reveals the rule, delta and reason behind the divergence.',
      },
      {
        title: 'Drill into the replay',
        body: 'Click a row to open the detail drawer for the underlying receipt and feed the finding back to the scenario owner.',
      },
    ],
    glossary: [
      { term: 'Matched', meaning: 'Historical alert reproduced byte-for-byte by the replay.' },
      { term: 'Missed', meaning: 'Historical alert that the replay failed to regenerate — investigate first.' },
      { term: 'Extra', meaning: 'Replay produced an alert that was not in history.' },
      { term: 'Threshold drift', meaning: 'Numerical difference inside the configured drift tolerance band.' },
    ],
  },

  'rule-drift': {
    eyebrow: '§ GETTING STARTED',
    title: 'Rule drift — detect quiet behavioural change',
    intro:
      'Drift is the weighted distance between a baseline run and today\'s run across four independent axes.',
    audience: 'For validation analysts + platform engineers',
    steps: [
      {
        title: 'Scan the timeline',
        body: 'The 90-day drift sparkline shows when a rule started moving. Dashed series = baseline, solid = observed.',
      },
      {
        title: 'Filter + investigate',
        body: 'Filter by status (Open / Review / Monitor) or engine; hit Investigate on a row to see the 4-dim breakdown drawer.',
      },
      {
        title: 'Dismiss when noisy',
        body: 'If a drift is expected noise, Dismiss removes it from the live queue while keeping the backend audit entry.',
      },
    ],
    glossary: [
      { term: 'Threshold', meaning: 'Shift in the numeric threshold that fires the rule.' },
      { term: 'Grouping', meaning: 'Change in which transactions roll up into the same alert.' },
      { term: 'Volume', meaning: 'Change in alert count vs. the baseline window.' },
      { term: 'Timing', meaning: 'Change in when within a window the rule fires.' },
    ],
  },

  'model-registry': {
    eyebrow: '§ GETTING STARTED',
    title: 'Model registry — pinned engine versions',
    intro:
      'Every production replay runs against a pinned engine version — a BLAKE3 digest that fixes rules, thresholds and reference data.',
    audience: 'For platform engineers + MLROs',
    steps: [
      {
        title: 'Inspect active models',
        body: 'Use the status pills to filter Prod / Staging / Training / Awaiting sign, and the Type select to focus on one family.',
      },
      {
        title: 'Register a new version',
        body: 'Hit "+ Register version" and paste the 64-char BLAKE3 manifest hash — the new row lands in STAGING for review.',
      },
      {
        title: 'Promote or retire',
        body: 'Promote moves a version to PROD (next replay uses it). Retire pulls it from rotation while preserving audit history.',
      },
    ],
    glossary: [
      { term: 'Manifest hash', meaning: '64-hex BLAKE3 digest pinning the engine configuration used by the model.' },
      { term: 'Staging', meaning: 'Version available for review; never runs production replays.' },
      { term: 'Prod', meaning: 'Version currently used by scheduled production replays.' },
      { term: 'Retired', meaning: 'Kept for audit replay, skipped by new runs.' },
    ],
  },

  'quorum-keys': {
    eyebrow: '§ GETTING STARTED',
    title: 'Quorum keys — the 3-of-5 signer set',
    intro:
      'Sentinel\'s attestations require signatures from 3 of 5 HSM-anchored ed25519 signers before they seal.',
    audience: 'For security engineers + compliance',
    steps: [
      {
        title: 'Review the live signer set',
        body: 'Each card shows a signer, their HSM provider and when the next scheduled rotation is due.',
      },
      {
        title: 'Rotate a key on schedule',
        body: 'Use Rotate for normal 90-day rotation or an out-of-band key swap. Paste the new ed25519 public key (hex).',
      },
      {
        title: 'Revoke on compromise',
        body: 'Revoke takes a signer offline immediately. Existing attestations stay verifiable; new ones need the remaining members.',
      },
    ],
    glossary: [
      { term: '3-of-5', meaning: 'Threshold scheme: any 3 of the 5 signers can seal an attestation.' },
      { term: 'Rotation', meaning: 'Scheduled or break-glass swap of a signer\'s public key.' },
      { term: 'Revocation', meaning: 'Immediate removal of a signer from the active set.' },
      { term: 'HSM', meaning: 'Hardware Security Module — the private key never leaves the HSM.' },
    ],
  },

  attestations: {
    eyebrow: '§ GETTING STARTED',
    title: 'Attestations — quorum-signed evidence receipts',
    intro:
      'Each attestation is a 3-of-5 signed, Merkle-rooted receipt that proves exactly which evidence was sealed and when.',
    audience: 'For compliance + MLROs',
    steps: [
      {
        title: 'Check pending signatures',
        body: 'Filter to Pending or Partial to see attestations still waiting on signers; share a secure link with the queue.',
      },
      {
        title: 'Verify + download',
        body: 'Per-row Verify re-checks signatures + Merkle root; Download emits the JSON payload for archival.',
      },
      {
        title: 'Seal a fresh attestation',
        body: 'Use "+ Seal attestation" to choose a scope (e.g. Q2 replay) and start collecting signatures from the quorum.',
      },
    ],
    glossary: [
      { term: 'Scope', meaning: 'What the attestation covers (e.g. "Q2 AML replay · rapid-movement").' },
      { term: 'Quorum', meaning: '3 of 5 signers required to seal — matches the HSM quorum.' },
      { term: 'Merkle root', meaning: 'Short hash that uniquely identifies the sealed evidence tree.' },
      { term: 'Revoke', meaning: 'Mark an attestation as no-longer valid (historical signatures remain verifiable).' },
    ],
  },

  'audit-packages': {
    eyebrow: '§ GETTING STARTED',
    title: 'Audit packages — regulator-ready bundles',
    intro:
      'Roll replay runs + their attestations into a single sealed bundle your auditor can verify offline.',
    audience: 'For MLROs + auditors',
    steps: [
      {
        title: 'Filter + review',
        body: 'Use the status and regulator filters to find drafts, sealed or shared packages; switch to Timeline for chronology.',
      },
      {
        title: 'Seal a new package',
        body: 'Hit "New package", name its scope + regulator, then submit — the package captures runs, attestations and manifest.',
      },
      {
        title: 'Share or export CSV',
        body: 'Per-row Download emits the signed archive; "Export CSV" exports the table for reporting outside Sentinel.',
      },
    ],
    glossary: [
      { term: 'Draft', meaning: 'Still being assembled; contents may change until sealed.' },
      { term: 'Sealed', meaning: 'Contents frozen, Merkle root published, quorum signatures attached.' },
      { term: 'Regulator', meaning: 'Target authority (FCA, OFAC, NCA, FinCEN, …) the package is prepared for.' },
      { term: 'Provenance', meaning: 'The full chain of runs, attestations and model versions inside the package.' },
    ],
  },

  integrations: {
    eyebrow: '§ GETTING STARTED',
    title: 'Integrations — adapters, fabric, sinks',
    intro:
      'Source adapters feed Sentinel, the fabric streams events, sinks export evidence — all wired through one typed contract.',
    audience: 'For platform engineers',
    steps: [
      {
        title: 'Filter to a category',
        body: 'Use the pills (Adapters / Fabric / Sinks) or the status select to find the integration you manage.',
      },
      {
        title: 'Inspect the row',
        body: 'Click a row to open the configuration pane with endpoint, auth mode and last-test result.',
      },
      {
        title: 'Enable / disable carefully',
        body: 'Toggling enable/disable affects live ingestion immediately; credentials stay inside the tenant HSM.',
      },
    ],
    glossary: [
      { term: 'Adapter', meaning: 'Source plugin that pulls data from an upstream engine (Oracle FCCM, Actimize, …).' },
      { term: 'Fabric', meaning: 'Event bus that streams canonical records between Sentinel components.' },
      { term: 'Sink', meaning: 'Downstream target for sealed evidence — object store, SIEM, regulator portal.' },
      { term: 'Certified', meaning: 'Integration passed the latest interoperability + auth probe.' },
    ],
  },

  coverage: {
    eyebrow: '§ GETTING STARTED',
    title: 'Coverage — scenario × window matrix',
    intro:
      'How many scenarios in each family got replayed in each window, with parity and open drift flags.',
    audience: 'For validation managers',
    steps: [
      {
        title: 'Pick a window',
        body: 'All windows / Q1 2026 / Q4 2025 tabs scope the matrix. Use the Band select to see only Pass / Warn / Fail cells.',
      },
      {
        title: 'Drill into a cell',
        body: 'Click a row to open the coverage-cell drawer — listing every replay run intersecting that family + window.',
      },
      {
        title: 'Export for reporting',
        body: 'The Download button emits a JSON snapshot of the current matrix you can attach to a review doc.',
      },
    ],
    glossary: [
      { term: 'Family', meaning: 'Scenario group (AML, Trade, Sanctions, Mule, Fraud, KYC).' },
      { term: 'Coverage %', meaning: 'Fraction of family scenarios replayed in the window.' },
      { term: 'Parity', meaning: 'Average byte-identical match rate across the cell\'s runs.' },
      { term: 'Band', meaning: 'Pass (≥75 %), Warn (50-74 %), Fail (<50 %).' },
    ],
  },

  audit: {
    eyebrow: '§ GETTING STARTED',
    title: 'Audit trail — append-only event log',
    intro:
      'Every operator action and every pipeline stage emits a canonical AuditEvent — this screen is the read-only log.',
    audience: 'For compliance + security engineers',
    steps: [
      {
        title: 'Filter by category',
        body: 'Replay / Attestation / Keys / Packages / AuthZ tabs show the slice you care about; time-range narrows further.',
      },
      {
        title: 'Search and inspect',
        body: 'Search matches actor, action and target strings. Click any row to pop the full event payload in a drawer.',
      },
      {
        title: 'Export JSONL',
        body: 'The Export button emits a newline-delimited JSON file suitable for archival, SIEM ingest or regulator hand-off.',
      },
    ],
    glossary: [
      { term: 'AuditEvent', meaning: 'Canonical, immutable record of a single operator or pipeline action.' },
      { term: 'Append-only', meaning: 'Rows are never edited or deleted — new state is a new row.' },
      { term: 'Actor', meaning: 'User, role or automated component that performed the action.' },
      { term: 'Target', meaning: 'Entity the action was performed against (run id, signer id, …).' },
    ],
  },

  'field-notes': {
    eyebrow: '§ GETTING STARTED',
    title: 'Field notes — operator knowledge base',
    intro:
      'Short, categorised write-ups from the operators running Sentinel — what worked, what broke, what to watch.',
    audience: 'For the whole validation team',
    steps: [
      {
        title: 'Browse by category',
        body: 'Engineering / Operations / Governance / Product / Research pills filter the list; use search for keywords.',
      },
      {
        title: 'Read the featured note',
        body: 'The featured card surfaces the most recent or most-important note — skim it before starting shift.',
      },
      {
        title: 'Publish a new note',
        body: 'Hit "+ New note" to draft, attach tags and authors, then Publish — the note appears in the list immediately.',
      },
    ],
    glossary: [
      { term: 'Featured', meaning: 'Pinned note shown at the top until a newer featured note is published.' },
      { term: 'Tags', meaning: 'Free-form labels used to group related notes (comma-separated).' },
      { term: 'Category', meaning: 'One of the 5 buckets — keeps the archive easy to skim.' },
      { term: 'Authors', meaning: 'Who to ping for follow-up questions on the note.' },
    ],
  },

  settings: {
    eyebrow: '§ GETTING STARTED',
    title: 'Settings — tenant + data-mode controls',
    intro:
      'Runtime configuration for the current tenant: data mode, region, retention buckets and the machine API key.',
    audience: 'For tenant admins',
    steps: [
      {
        title: 'Pick a data mode',
        body: 'Live (real feeds), Hybrid (live + synthetic drift) or Synthetic (pilot/demo). Changes take effect on next replay.',
      },
      {
        title: 'Tune retention + residency',
        body: 'Toggle retention buckets on/off and choose EU / US region + sovereign or multi-region residency.',
      },
      {
        title: 'Save — or rotate the API key',
        body: 'Unsaved edits show a dot on the field; hit "Save changes" to commit. Use "Rotate key" for an immediate API-key swap.',
      },
    ],
    glossary: [
      { term: 'Live', meaning: 'Replay against in-region vendor feeds — production default.' },
      { term: 'Hybrid', meaning: 'Replay + synthetic drift injection for stress testing.' },
      { term: 'Synthetic', meaning: 'Pilot / demo mode — no real customer data leaves the tenant.' },
      { term: 'Sovereign', meaning: 'All evidence bytes stay inside the primary region.' },
    ],
  },

  team: {
    eyebrow: '§ GETTING STARTED',
    title: 'Team & roles — who can do what',
    intro:
      'Six typed roles gate every action. Invite operators, change roles and suspend access from this screen.',
    audience: 'For tenant admins',
    steps: [
      {
        title: 'Filter + search',
        body: 'Use the role filter or search by email/name to find an operator; click a row for full profile + status history.',
      },
      {
        title: 'Invite a new operator',
        body: 'Hit "+ Invite operator" — email + role + region + MFA mode. They receive a time-boxed invite link via tenant HSM.',
      },
      {
        title: 'Change role or suspend',
        body: 'Per-row controls let you change role (role-change is logged) or suspend access for incident response.',
      },
    ],
    glossary: [
      { term: 'Validation analyst', meaning: 'Triages alerts, runs replays, reviews divergences.' },
      { term: 'Validation manager', meaning: 'Signs off on validation campaigns and promotes scenarios.' },
      { term: 'MLRO', meaning: 'Money-Laundering Reporting Officer — approves regulator-facing packages.' },
      { term: 'Compliance reviewer', meaning: 'Reviews attestations and ticks off control evidence.' },
      { term: 'Auditor', meaning: 'Read-only access for external or internal audit.' },
    ],
  },

  auth: {
    eyebrow: '§ GETTING STARTED',
    title: 'Sign in — operator console access',
    intro:
      'Three flows: sign in, request access (new operators), or recover credentials. Real SSO lands alongside your IdP.',
    audience: 'For anyone opening the console',
    steps: [
      {
        title: 'Pick a flow',
        body: 'Tabs switch between Sign in, Request access and Forgot password — only one form is mounted at a time.',
      },
      {
        title: 'Sign in with demo creds',
        body: 'Use test@test.com / password123 / 482107 for the pilot demo; production uses tenant SSO or hardware keys.',
      },
      {
        title: 'Trust this device (optional)',
        body: '30-day device trust skips re-prompting on the same browser; the token persists in local storage, not cookies.',
      },
    ],
    glossary: [
      { term: 'TOTP', meaning: 'Time-based One-Time Password — 6-digit code from an authenticator app.' },
      { term: 'SSO', meaning: 'Single Sign-On via SAML or OIDC through your tenant IdP.' },
      { term: 'WebAuthn', meaning: 'Hardware key sign-in (YubiKey / platform authenticator).' },
      { term: 'Magic link', meaning: 'Short-lived recovery link signed by the tenant HSM.' },
    ],
  },
};
